import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  approveZipSubmission,
  batchApproveZipSubmissions,
  fetchZipSubmissions,
  rejectZipSubmission,
} from '@src/api/actions/dashboard/zipSubmissions';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import paths from '@src/paths';
import PushToFacebookModal from './PushToFacebookModal';
import SubmissionDiffMap from './components/SubmissionDiffMap';

export default function ZipSubmissionsPage() {
  // Parent company filter
  const {
    options: parentCompanyOptions,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete();
  const [filterParentCompanyId, setFilterParentCompanyId] = useState('');

  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({
    parentCompanyId: filterParentCompanyId || undefined,
  });

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  // Expanded rows — pending auto-expanded
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [manualCollapsed, setManualCollapsed] = useState(new Set());

  // Sorting
  const [sortField, setSortField] = useState(''); // '', 'pct_changed', 'net_pop', 'days_pending'
  const [sortDir, setSortDir] = useState('desc');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Reject dialog — supports single submission or a batch
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectIds, setRejectIds] = useState([]);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  // Push to Facebook modal — ONE consolidated push per client. The modal
  // collapses every approved submission into one push per vertical, so all
  // entry points just hand it (client, verticals) derived from submissions.
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTarget, setPushTarget] = useState(null); // {clientId, verticalIds}

  const openPushModal = (subOrSubs) => {
    const subs = Array.isArray(subOrSubs) ? subOrSubs : [subOrSubs];
    if (subs.length === 0) return;
    setPushTarget({
      clientId: subs[0].client,
      verticalIds: [...new Set(subs.map((s) => s.vertical))],
    });
    setPushModalOpen(true);
  };

  const handlePushModalClose = () => {
    setPushModalOpen(false);
    setPushTarget(null);
  };

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadSubmissions = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (filterParentCompanyId) params.parent_company_id = filterParentCompanyId;
      if (filterClientId) params.client_id = filterClientId;
      if (filterStatus) params.status = filterStatus;
      const res = await fetchZipSubmissions(params);
      const results = res.data?.results || [];
      setSubmissions(results);
      setTotalCount(res.data?.count || 0);
      setTotalPages(Math.ceil((res.data?.count || 0) / 50));

      // Auto-expand pending rows (single submissions and batches)
      const pendingKeys = new Set();
      const seenBatches = new Set();
      results.forEach((s) => {
        if (s.status !== 'pending') return;
        if (s.batch_id) {
          if (seenBatches.has(s.batch_id)) return;
          seenBatches.add(s.batch_id);
          pendingKeys.add(`batch:${s.batch_id}`);
        } else {
          pendingKeys.add(s.id);
        }
      });
      setExpandedIds(pendingKeys);
      setManualCollapsed(new Set());
      setSelectedIds(new Set());
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [filterParentCompanyId, filterClientId, filterStatus, page]);

  // loadSubmissions' identity tracks filters + page, so one effect covers
  // both. Filter handlers reset page to 1 themselves; the old paired
  // filter/page effects fired a duplicate request on every mount.
  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // Drop acted-on submissions from the list in place (no refetch) so the
  // table updates instantly. Rows are keyed by submission id; selection and
  // expand state are keyed the same way, so we prune them by id too. Stale
  // `batch:` expand keys for emptied batches are harmless (nothing renders
  // them) and get cleared on the next real load.
  const removeSubmissionsLocally = useCallback((ids) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    if (idSet.size === 0) return;
    setSubmissions((prev) => prev.filter((s) => !idSet.has(s.id)));
    setTotalCount((prev) => Math.max(0, prev - idSet.size));
    const dropIds = (prev) => {
      const next = new Set(prev);
      idSet.forEach((id) => next.delete(id));
      return next;
    };
    setSelectedIds(dropIds);
    setExpandedIds(dropIds);
    setManualCollapsed(dropIds);
  }, []);

  // Quick stats
  const stats = useMemo(() => {
    const pending = submissions.filter((s) => s.status === 'pending').length;
    const approved = submissions.filter((s) => s.status === 'approved').length;
    const rejected = submissions.filter((s) => s.status === 'rejected').length;
    return { pending, approved, rejected, total: totalCount };
  }, [submissions, totalCount]);

  const pendingSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'pending'),
    [submissions]
  );

  // Batch grouping — detect submissions sharing a batch_id
  const pendingBatches = useMemo(() => {
    const batches = {};
    pendingSubmissions.forEach((s) => {
      if (s.batch_id) {
        if (!batches[s.batch_id]) batches[s.batch_id] = [];
        batches[s.batch_id].push(s);
      }
    });
    // Only return batches with 2+ submissions
    return Object.fromEntries(Object.entries(batches).filter(([, subs]) => subs.length > 1));
  }, [pendingSubmissions]);

  // Group submissions by batch_id so multi-vertical submissions render as
  // one row per (territory, batch). Single submissions and unbatched rows
  // are wrapped as kind: 'single' to keep render code uniform.
  const submissionGroups = useMemo(() => {
    const order = [];
    const byBatch = {};
    submissions.forEach((s) => {
      if (s.batch_id) {
        if (!byBatch[s.batch_id]) {
          byBatch[s.batch_id] = { kind: 'batch', batchId: s.batch_id, submissions: [] };
          order.push({ key: `batch:${s.batch_id}`, ref: byBatch[s.batch_id] });
        }
        byBatch[s.batch_id].submissions.push(s);
      } else {
        order.push({ key: `sub:${s.id}`, ref: { kind: 'single', submission: s } });
      }
    });
    // Demote batches of 1 down to 'single' so they look the same as
    // pre-batching submissions.
    return order.map((entry) => {
      const r = entry.ref;
      if (r.kind === 'batch' && r.submissions.length === 1) {
        return { key: `sub:${r.submissions[0].id}`, kind: 'single', submission: r.submissions[0] };
      }
      if (r.kind === 'batch') {
        return { key: entry.key, ...r };
      }
      return { key: entry.key, ...r };
    });
  }, [submissions]);

  // Aggregate impact for a batch group so the collapsed row shows totals.
  const batchAggregate = useCallback((subs) => {
    let addCount = 0;
    let removeCount = 0;
    let netPop = 0;
    let maxPct = 0;
    let oldest = null;
    let warnings = [];
    let allSameStatus = true;
    let firstStatus = subs[0]?.status;
    subs.forEach((s) => {
      addCount += s.add_count || 0;
      removeCount += s.remove_count || 0;
      const imp = s.impact_summary || {};
      netPop += imp.net_population_change || 0;
      maxPct = Math.max(maxPct, imp.pct_zips_changed || 0);
      warnings = warnings.concat(imp.warnings || []);
      const t = new Date(s.submitted_at);
      if (!oldest || t < oldest) oldest = t;
      if (s.status !== firstStatus) allSameStatus = false;
    });
    return {
      addCount,
      removeCount,
      netPop,
      maxPct,
      submittedAt: oldest ? oldest.toISOString() : null,
      warnings,
      allSameStatus,
      status: allSameStatus ? firstStatus : 'mixed',
    };
  }, []);

  // Sorting: works against groups (batches collapse to their aggregate).
  const sortedGroups = useMemo(() => {
    const valueOf = (group, field) => {
      if (group.kind === 'batch') {
        const agg = batchAggregate(group.submissions);
        if (field === 'pct_changed') return agg.maxPct;
        if (field === 'net_pop') return agg.netPop;
        if (field === 'days_pending') return new Date(agg.submittedAt).getTime();
        return 0;
      }
      const s = group.submission;
      if (field === 'pct_changed') return s.impact_summary?.pct_zips_changed || 0;
      if (field === 'net_pop') return s.impact_summary?.net_population_change || 0;
      if (field === 'days_pending') return new Date(s.submitted_at).getTime();
      return 0;
    };
    if (!sortField) return submissionGroups;
    const sorted = [...submissionGroups].sort((a, b) => {
      const av = valueOf(a, sortField);
      const bv = valueOf(b, sortField);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [submissionGroups, sortField, sortDir, batchAggregate]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setManualCollapsed((mc) => new Set([...mc, id]));
      } else {
        next.add(id);
        setManualCollapsed((mc) => {
          const nm = new Set(mc);
          nm.delete(id);
          return nm;
        });
      }
      return next;
    });
  };

  const isExpanded = (id) => expandedIds.has(id) && !manualCollapsed.has(id);

  // ─── Selection ─────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleBatchSelect = (subs) => {
    const ids = subs.filter((s) => s.status === 'pending').map((s) => s.id);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingSubmissions.map((s) => s.id)));
    }
  };

  // ─── Actions ───────────────────────────────────────────────────────

  const handleApprove = async (submissionId) => {
    const sub = submissions.find((s) => s.id === submissionId);
    const warnings = sub?.impact_summary?.warnings || [];
    const warningLines = warnings.map((w) => `⚠ ${w.message}`).join('\n\n');
    const prompt = warningLines
      ? `${warningLines}\n\nApprove anyway? ZIP changes will be applied immediately.`
      : 'Approve this submission? ZIP changes will be applied immediately.';
    if (!window.confirm(prompt)) {
      return;
    }
    setActionLoading(submissionId);
    try {
      await approveZipSubmission(submissionId);
      showSnack('Submission approved. ZIP changes applied and downstream tasks queued.');
      removeSubmissionsLocally([submissionId]);
      if (sub) openPushModal(sub);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve.';
      showSnack(detail, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkApprove = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} submission${ids.length > 1 ? 's' : ''}? ZIP changes will be applied immediately.`)) {
      return;
    }
    setBulkApproving(true);
    const succeededIds = [];
    let failCount = 0;
    for (const id of ids) {
      try {
        await approveZipSubmission(id);
        succeededIds.push(id);
      } catch {
        failCount += 1;
      }
    }
    setBulkApproving(false);
    const successCount = succeededIds.length;
    if (failCount > 0) {
      showSnack(`${successCount} approved, ${failCount} failed.`, 'warning');
    } else {
      showSnack(`${successCount} submission${successCount > 1 ? 's' : ''} approved.`);
    }
    removeSubmissionsLocally(succeededIds);
    // Offer one consolidated push when everything approved belongs to a
    // single client (the common multi-territory / multi-vertical case).
    const approvedSubs = submissions.filter((s) => succeededIds.includes(s.id));
    const clientIds = new Set(approvedSubs.map((s) => s.client));
    if (failCount === 0 && clientIds.size === 1) {
      openPushModal(approvedSubs);
    }
  };

  const handleBatchApprove = async (batchId) => {
    const batchSubs = pendingBatches[batchId] || [];
    if (!window.confirm(`Approve all ${batchSubs.length} submissions in this batch? ZIP changes will be applied immediately.`)) {
      return;
    }
    setBulkApproving(true);
    try {
      await batchApproveZipSubmissions(batchId);
      showSnack(`Batch of ${batchSubs.length} submissions approved.`);
      removeSubmissionsLocally(batchSubs.map((s) => s.id));
      // One modal, one push — the backend collapses the whole batch into a
      // single push per vertical covering every territory.
      openPushModal(batchSubs);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve batch.';
      showSnack(detail, 'error');
    } finally {
      setBulkApproving(false);
    }
  };

  const handleRejectOpen = (idOrIds) => {
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    setRejectIds(ids);
    setRejectNotes('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (rejectIds.length === 0) return;
    setRejectSaving(true);
    const succeededIds = [];
    let fail = 0;
    for (const id of rejectIds) {
      try {
        await rejectZipSubmission(id, { reviewer_notes: rejectNotes });
        succeededIds.push(id);
      } catch {
        fail += 1;
      }
    }
    setRejectSaving(false);
    setRejectDialogOpen(false);
    const success = succeededIds.length;
    if (fail > 0) {
      showSnack(`${success} rejected, ${fail} failed.`, 'warning');
    } else {
      showSnack(
        rejectIds.length > 1
          ? `${success} submissions rejected.`
          : 'Submission rejected.',
      );
    }
    removeSubmissionsLocally(succeededIds);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4">ZIP Submissions</Typography>
          {stats.pending > 0 && (
            <Chip
              label={`${stats.pending} pending`}
              color="warning"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* ─── Quick Stats ──────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} mb={2}>
        <StatChip label="Total" value={stats.total} />
        <StatChip label="Pending" value={stats.pending} color="warning" />
        <StatChip label="Approved" value={stats.approved} color="success" />
        <StatChip label="Rejected" value={stats.rejected} color="error" />
      </Stack>

      <Card>
        <CardContent>
          {/* Filters */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            {parentCompanyOptions.length > 0 && (
              <Autocomplete
                options={parentCompanyOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={findParentCompanyByValue(filterParentCompanyId)}
                onChange={(_, val) => {
                  setFilterParentCompanyId(val ? String(val.value) : '');
                  setFilterClientId('');
                  setPage(1);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Parent Company" size="small" sx={{ minWidth: 220 }} />
                )}
              />
            )}
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findOptionByValue(filterClientId)}
              onChange={(_, val) => {
                setFilterClientId(val ? String(val.value) : '');
                setPage(1);
              }}
              onInputChange={handleClientSearch}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" sx={{ minWidth: 220 }} />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => loadSubmissions(1)} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>

            {/* Bulk approve */}
            {selectedIds.size > 0 && (
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkApprove}
                disabled={bulkApproving}
                startIcon={bulkApproving ? <CircularProgress size={16} /> : null}
              >
                Approve {selectedIds.size} Selected
              </Button>
            )}
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : submissions.length === 0 ? (
            <Alert severity="info">No submissions found.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        {pendingSubmissions.length > 0 && (
                          <Checkbox
                            size="small"
                            checked={selectedIds.size === pendingSubmissions.length && pendingSubmissions.length > 0}
                            indeterminate={selectedIds.size > 0 && selectedIds.size < pendingSubmissions.length}
                            onChange={toggleSelectAll}
                          />
                        )}
                      </TableCell>
                      <TableCell width={40} />
                      <TableCell>Client</TableCell>
                      <TableCell>Territory</TableCell>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Adds</TableCell>
                      <TableCell>Removes</TableCell>
                      <SortableHeader field="net_pop" label="Net Pop" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader field="pct_changed" label="% Changed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Submitted By</TableCell>
                      <SortableHeader field="days_pending" label="Submitted" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedGroups.map((group) => {
                      if (group.kind === 'batch') {
                        return renderBatchRows({
                          group,
                          batchAggregate,
                          isExpanded,
                          toggleExpand,
                          toggleBatchSelect,
                          selectedIds,
                          handleBatchApprove,
                          handleApprove,
                          handleRejectOpen,
                          openPushModal,
                          actionLoading,
                          bulkApproving,
                        });
                      }
                      const sub = group.submission;
                      const expanded = isExpanded(sub.id);
                      const isPending = sub.status === 'pending';
                      const isSelected = selectedIds.has(sub.id);
                      const impact = sub.impact_summary;
                      const pctChanged = impact?.pct_zips_changed || 0;
                      const netPop = impact?.net_population_change || 0;
                      const warnings = impact?.warnings || [];
                      const topWarning = warnings[0] || null;
                      const isHighImpact = !!topWarning || pctChanged > 20;
                      const daysPending = isPending ? getDaysPending(sub.submitted_at) : null;

                      return [
                        <TableRow
                          key={sub.id}
                          hover
                          sx={isPending ? { bgcolor: 'warning.50' } : undefined}
                        >
                          <TableCell padding="checkbox">
                            {isPending && (
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleSelect(sub.id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(sub.id)}>
                              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{sub.client_name}</span>
                              {isHighImpact && (
                                <Tooltip
                                  title={
                                    topWarning
                                      ? topWarning.message
                                      : `High impact: ${pctChanged}% of territory changing`
                                  }
                                >
                                  <WarningAmberIcon
                                    sx={{
                                      fontSize: 16,
                                      color: topWarning?.code === 'would_empty_territory'
                                        ? 'error.main'
                                        : 'warning.main',
                                    }}
                                  />
                                </Tooltip>
                              )}
                              <Tooltip title="View client ZIP coverage">
                                <IconButton
                                  size="small"
                                  href={`${paths.zipCoverage}?client_id=${sub.client}`}
                                  target="_blank"
                                >
                                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>{sub.territory_name || '-'}</TableCell>
                          <TableCell>{sub.vertical_name}</TableCell>
                          <TableCell>
                            <Chip label={sub.add_count} size="small" color="success" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={sub.remove_count} size="small" color="error" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {netPop !== 0 ? (
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={netPop > 0 ? 'success.main' : 'error.main'}
                              >
                                {netPop > 0 ? '+' : ''}{netPop.toLocaleString()}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={isHighImpact ? 'warning.main' : 'text.primary'}
                            >
                              {pctChanged}%
                            </Typography>
                          </TableCell>
                          <TableCell>{sub.submitted_by_email}</TableCell>
                          <TableCell>
                            <Stack spacing={0}>
                              <DaysPendingLabel daysPending={daysPending} submittedAt={sub.submitted_at} />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                label={sub.status}
                                size="small"
                                color={
                                  sub.status === 'pending' ? 'warning'
                                  : sub.status === 'approved' ? 'success'
                                  : sub.status === 'rejected' ? 'error'
                                  : 'default'
                                }
                              />
                              {sub.batch_id && pendingBatches[sub.batch_id] && (
                                <Chip label="batch" size="small" variant="outlined" />
                              )}
                              {sub.status === 'approved' && !sub.pushed_at && (
                                <Tooltip title="Approved but the updated ZIP set has not been pushed to Facebook yet">
                                  <Chip label="not pushed" size="small" color="warning" variant="outlined" />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {isPending && (
                              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                {sub.batch_id && pendingBatches[sub.batch_id] && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleBatchApprove(sub.batch_id)}
                                    disabled={bulkApproving}
                                  >
                                    Approve Batch ({pendingBatches[sub.batch_id].length})
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleApprove(sub.id)}
                                  disabled={actionLoading === sub.id}
                                >
                                  {actionLoading === sub.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    'Approve'
                                  )}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRejectOpen(sub.id)}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            )}
                            {sub.status === 'approved' && (
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                {sub.reviewed_by_email && (
                                  <Typography variant="caption" color="text.secondary">
                                    by {sub.reviewed_by_email}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openPushModal(sub)}
                                >
                                  Push FB
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>,

                        /* Expanded row with items */
                        <TableRow key={`${sub.id}-detail`}>
                          <TableCell colSpan={14} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
                            <Collapse in={expanded} mountOnEnter unmountOnExit>
                              <Box p={2} bgcolor="action.hover">
                                {sub.submitter_notes && (
                                  <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                                    Client note: {sub.submitter_notes}
                                  </Alert>
                                )}
                                {sub.reviewer_notes && (
                                  <Alert severity="info" sx={{ mb: 1.5 }}>
                                    Reviewer note: {sub.reviewer_notes}
                                  </Alert>
                                )}
                                {sub.impact_summary && (
                                  <ImpactSummary impact={sub.impact_summary} addCount={sub.add_count} removeCount={sub.remove_count} />
                                )}
                                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                  <Box sx={{ width: 380, flexShrink: 0 }}>
                                    <SubmissionDiffMap items={sub.items || []} height={240} />
                                  </Box>
                                  <Stack direction="row" spacing={4}>
                                  <Box>
                                    {(() => {
                                      const adds = (sub.items || []).filter((i) => i.action === 'add');
                                      const addPop = adds.reduce((s, i) => s + (i.population || 0), 0);
                                      return (
                                        <Typography variant="subtitle2" gutterBottom>
                                          Adds ({adds.length}){addPop > 0 ? ` \u2022 ${addPop.toLocaleString()} pop.` : ''}
                                        </Typography>
                                      );
                                    })()}
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                      {(sub.items || [])
                                        .filter((i) => i.action === 'add')
                                        .map((item) => (
                                          <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                                            <Chip
                                              label={item.zip || item.zip_raw}
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                            />
                                          </Tooltip>
                                        ))}
                                      {(sub.items || []).filter((i) => i.action === 'add').length === 0 && (
                                        <Typography variant="body2" color="text.secondary">None</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box>
                                    {(() => {
                                      const removes = (sub.items || []).filter((i) => i.action === 'remove');
                                      const removePop = removes.reduce((s, i) => s + (i.population || 0), 0);
                                      return (
                                        <Typography variant="subtitle2" gutterBottom>
                                          Removes ({removes.length}){removePop > 0 ? ` \u2022 ${removePop.toLocaleString()} pop.` : ''}
                                        </Typography>
                                      );
                                    })()}
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                      {(sub.items || [])
                                        .filter((i) => i.action === 'remove')
                                        .map((item) => (
                                          <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                                            <Chip
                                              label={item.zip || item.zip_raw}
                                              size="small"
                                              color="error"
                                              variant="outlined"
                                            />
                                          </Tooltip>
                                        ))}
                                      {(sub.items || []).filter((i) => i.action === 'remove').length === 0 && (
                                        <Typography variant="body2" color="text.secondary">None</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  </Stack>
                                </Stack>
                                {sub.reviewed_at && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                    Reviewed {new Date(sub.reviewed_at).toLocaleDateString()} by {sub.reviewed_by_email || 'staff'}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>,
                      ];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {rejectIds.length > 1
            ? `Reject ${rejectIds.length} Submissions (Batch)`
            : 'Reject Submission'}
        </DialogTitle>
        <DialogContent>
          {rejectIds.length > 1 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The same reason will apply to all {rejectIds.length} submissions in this batch.
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason / Notes (visible to client)"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={rejectSaving}
          >
            {rejectSaving ? <CircularProgress size={18} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push to Facebook Modal */}
      <PushToFacebookModal
        open={pushModalOpen}
        onClose={handlePushModalClose}
        clientId={pushTarget?.clientId}
        verticalIds={pushTarget?.verticalIds}
        onPushComplete={() => {
          showSnack('Push to Facebook complete.');
          loadSubmissions(page);
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function renderBatchRows({
  group,
  batchAggregate,
  isExpanded,
  toggleExpand,
  toggleBatchSelect,
  selectedIds,
  handleBatchApprove,
  handleApprove,
  handleRejectOpen,
  openPushModal,
  actionLoading,
  bulkApproving,
}) {
  const subs = group.submissions;
  const anchor = subs[0];
  const agg = batchAggregate(subs);
  const expandKey = `batch:${group.batchId}`;
  const expanded = isExpanded(expandKey);
  const pendingIds = subs.filter((s) => s.status === 'pending').map((s) => s.id);
  const allPendingSelected = pendingIds.length > 0
    && pendingIds.every((id) => selectedIds.has(id));
  const someSelected = pendingIds.some((id) => selectedIds.has(id));
  const allPending = subs.every((s) => s.status === 'pending');
  const allApproved = subs.every((s) => s.status === 'approved');
  const isHighImpact = agg.warnings.length > 0 || agg.maxPct > 20;
  const topWarning = agg.warnings[0] || null;
  const daysPending = allPending ? getDaysPending(agg.submittedAt) : null;
  const verticalNames = subs.map((s) => s.vertical_name).filter(Boolean);
  const submittedByEmails = [
    ...new Set(subs.map((s) => s.submitted_by_email).filter(Boolean)),
  ];

  return [
    <TableRow
      key={expandKey}
      hover
      sx={allPending ? { bgcolor: 'warning.50' } : undefined}
    >
      <TableCell padding="checkbox">
        {pendingIds.length > 0 && (
          <Checkbox
            size="small"
            checked={allPendingSelected}
            indeterminate={!allPendingSelected && someSelected}
            onChange={() => toggleBatchSelect(subs)}
          />
        )}
      </TableCell>
      <TableCell>
        <IconButton size="small" onClick={() => toggleExpand(expandKey)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{anchor.client_name}</span>
          {isHighImpact && (
            <Tooltip
              title={
                topWarning
                  ? topWarning.message
                  : `High impact: up to ${agg.maxPct}% of a vertical's territory changing`
              }
            >
              <WarningAmberIcon
                sx={{
                  fontSize: 16,
                  color: topWarning?.code === 'would_empty_territory'
                    ? 'error.main'
                    : 'warning.main',
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="View client ZIP coverage">
            <IconButton
              size="small"
              href={`${paths.zipCoverage}?client_id=${anchor.client}`}
              target="_blank"
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>{anchor.territory_name || '-'}</TableCell>
      <TableCell>
        <Tooltip title={verticalNames.join(', ')}>
          <Chip
            label={`${subs.length} verticals`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        <Chip label={agg.addCount} size="small" color="success" variant="outlined" />
      </TableCell>
      <TableCell>
        <Chip label={agg.removeCount} size="small" color="error" variant="outlined" />
      </TableCell>
      <TableCell>
        {agg.netPop !== 0 ? (
          <Typography
            variant="body2"
            fontWeight={600}
            color={agg.netPop > 0 ? 'success.main' : 'error.main'}
          >
            {agg.netPop > 0 ? '+' : ''}{agg.netPop.toLocaleString()}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title="Highest % change across the verticals in this batch">
          <Typography
            variant="body2"
            fontWeight={600}
            color={isHighImpact ? 'warning.main' : 'text.primary'}
          >
            {agg.maxPct}%
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {submittedByEmails.length === 1
          ? submittedByEmails[0]
          : `${submittedByEmails.length} submitters`}
      </TableCell>
      <TableCell>
        <Stack spacing={0}>
          <DaysPendingLabel daysPending={daysPending} submittedAt={agg.submittedAt} />
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            label={agg.status}
            size="small"
            color={
              agg.status === 'pending' ? 'warning'
              : agg.status === 'approved' ? 'success'
              : agg.status === 'rejected' ? 'error'
              : 'default'
            }
          />
          <Chip label={`batch · ${subs.length}`} size="small" variant="outlined" />
        </Stack>
      </TableCell>
      <TableCell align="right">
        {allPending && (
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleBatchApprove(group.batchId)}
              disabled={bulkApproving}
              startIcon={bulkApproving ? <CircularProgress size={14} /> : null}
            >
              Approve Batch ({subs.length})
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleRejectOpen(pendingIds)}
            >
              Reject Batch
            </Button>
          </Stack>
        )}
        {!allPending && !allApproved && (
          <Typography variant="caption" color="text.secondary">
            mixed — expand to act per vertical
          </Typography>
        )}
        {allApproved && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openPushModal(subs)}
          >
            Push FB
          </Button>
        )}
      </TableCell>
    </TableRow>,

    <TableRow key={`${expandKey}-detail`}>
      <TableCell colSpan={14} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
        <Collapse in={expanded} mountOnEnter unmountOnExit>
          <Box p={2} bgcolor="action.hover">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              This batch was submitted as one action and applies to {subs.length} verticals.
            </Typography>
            <Box sx={{ maxWidth: 640, mb: 1.5 }}>
              <SubmissionDiffMap items={batchMapItems(subs)} height={240} />
            </Box>
            <Stack spacing={1.5}>
              {subs.map((s) => {
                const adds = (s.items || []).filter((i) => i.action === 'add');
                const removes = (s.items || []).filter((i) => i.action === 'remove');
                const isPending = s.status === 'pending';
                return (
                  <Box
                    key={s.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                      <Typography variant="subtitle2" fontWeight={600}>
                        {s.vertical_name}
                      </Typography>
                      <Chip
                        label={s.status}
                        size="small"
                        color={
                          s.status === 'pending' ? 'warning'
                          : s.status === 'approved' ? 'success'
                          : s.status === 'rejected' ? 'error'
                          : 'default'
                        }
                      />
                      <Chip label={`+${s.add_count}`} size="small" color="success" variant="outlined" />
                      <Chip label={`-${s.remove_count}`} size="small" color="error" variant="outlined" />
                      <Box flexGrow={1} />
                      {isPending && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleApprove(s.id)}
                            disabled={actionLoading === s.id}
                          >
                            {actionLoading === s.id ? <CircularProgress size={14} /> : 'Approve only this'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleRejectOpen(s.id)}
                          >
                            Reject only this
                          </Button>
                        </>
                      )}
                    </Stack>
                    {s.submitter_notes && (
                      <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                        Client note: {s.submitter_notes}
                      </Alert>
                    )}
                    {s.reviewer_notes && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Reviewer note: {s.reviewer_notes}
                      </Alert>
                    )}
                    {s.impact_summary && (
                      <ImpactSummary
                        impact={s.impact_summary}
                        addCount={s.add_count}
                        removeCount={s.remove_count}
                      />
                    )}
                    <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Adds ({adds.length})
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {adds.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          ) : adds.map((item) => (
                            <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                              <Chip
                                label={item.zip || item.zip_raw}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Removes ({removes.length})
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {removes.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          ) : removes.map((item) => (
                            <Tooltip key={item.id} title={item.city && item.state ? `${item.city}, ${item.state}` : ''} arrow>
                              <Chip
                                label={item.zip || item.zip_raw}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    </Stack>
                    {s.reviewed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Reviewed {new Date(s.reviewed_at).toLocaleDateString()} by {s.reviewed_by_email || 'staff'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>,
  ];
}

function StatChip({ label, value, color = 'default' }) {
  return (
    <Chip
      label={`${label}: ${value}`}
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600 }}
    />
  );
}

// A batch fans the same ZIP changes out to several verticals, so its
// submissions mostly share items — dedupe by (action, zip) for one map.
function batchMapItems(subs) {
  const seen = new Set();
  const items = [];
  subs.forEach((s) => {
    (s.items || []).forEach((i) => {
      const key = `${i.action}:${i.zip || i.zip_raw}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push(i);
    });
  });
  return items;
}

function getDaysPending(submittedAt) {
  const now = new Date();
  const submitted = new Date(submittedAt);
  return Math.floor((now - submitted) / (1000 * 60 * 60 * 24));
}

function DaysPendingLabel({ daysPending, submittedAt }) {
  const date = new Date(submittedAt);
  if (daysPending === null) {
    return (
      <Typography variant="body2">
        {date.toLocaleDateString()}{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Typography>
    );
  }

  let color = 'text.secondary';
  if (daysPending >= 7) color = 'error.main';
  else if (daysPending >= 3) color = 'warning.main';

  const label = daysPending === 0 ? 'Today' : daysPending === 1 ? '1d ago' : `${daysPending}d ago`;

  return (
    <>
      <Typography variant="body2">
        {date.toLocaleDateString()}
      </Typography>
      <Typography variant="caption" fontWeight={600} color={color}>
        {label}
      </Typography>
    </>
  );
}

function SortableHeader({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <TableCell
      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(field)}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <span>{label}</span>
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          )
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14, opacity: 0.25 }} />
        )}
      </Stack>
    </TableCell>
  );
}

function ImpactSummary({ impact, addCount, removeCount }) {
  const {
    current_zip_count,
    current_population,
    after_zip_count,
    after_population,
    add_population,
    remove_population,
    net_population_change,
    pct_zips_changed,
  } = impact;

  const fmt = (n) => (n || 0).toLocaleString();
  const netZips = addCount - removeCount;
  const netZipSign = netZips >= 0 ? '+' : '';
  const netPopSign = net_population_change >= 0 ? '+' : '';

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
        Impact Summary
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {/* ZIP count before → after */}
        <Box>
          <Typography variant="caption" color="text.secondary">ZIPs</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600}>{fmt(current_zip_count)}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>{fmt(after_zip_count)}</Typography>
            <Typography
              variant="body2"
              color={netZips > 0 ? 'success.main' : netZips < 0 ? 'error.main' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              ({netZipSign}{fmt(netZips)})
            </Typography>
          </Stack>
        </Box>

        {/* Population before → after */}
        <Box>
          <Typography variant="caption" color="text.secondary">Population</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600}>{fmt(current_population)}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>{fmt(after_population)}</Typography>
            <Typography
              variant="body2"
              color={net_population_change > 0 ? 'success.main' : net_population_change < 0 ? 'error.main' : 'text.secondary'}
              sx={{ ml: 0.5 }}
            >
              ({netPopSign}{fmt(net_population_change)})
            </Typography>
          </Stack>
        </Box>

        {/* Add / Remove population breakdown */}
        <Box>
          <Typography variant="caption" color="text.secondary">Add Pop / Remove Pop</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="success.main" fontWeight={600}>+{fmt(add_population)}</Typography>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" color="error.main" fontWeight={600}>-{fmt(remove_population)}</Typography>
          </Stack>
        </Box>

        {/* % territory changed */}
        <Box>
          <Typography variant="caption" color="text.secondary">Territory Changed</Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color={pct_zips_changed > 20 ? 'warning.main' : 'text.primary'}
          >
            {pct_zips_changed}%
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
